import { cn } from 'cn'
import type { ReactNode } from 'react'
import AlertIcon from '@/assets/images/alert.png'
import SuccessIcon from '@/assets/images/success.png'
import { Button } from '@/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/dialog'

type ConfirmDialogIconVariant = 'warning' | 'success'

interface ConfirmDialogProps {
  readonly open: boolean
  readonly onOpenChange?: (open: boolean) => void
  readonly title: string
  readonly description?: ReactNode
  /** 'warning' = alert image, 'success' = success image */
  readonly iconVariant?: ConfirmDialogIconVariant
  /** 'primary' (default) = blue title; 'default' = dark foreground title */
  readonly titleVariant?: 'primary' | 'default'
  readonly primaryLabel: string
  readonly onPrimary: () => void
  readonly secondaryLabel?: string
  readonly onSecondary?: () => void
  /** Icon rendered inside the secondary button */
  readonly secondaryIcon?: ReactNode
  /** data-tour anchor on the dialog wrapper */
  readonly dataTour?: string
  /** data-tour anchor on the icon/title/description block only, excluding the
   *  action buttons. A coach-mark spotlighting the whole dialog on a 360x800
   *  device leaves the card ~67px of content area against ~106px of copy, so
   *  the step text is clipped; anchoring here lifts the spotlight's lower edge
   *  clear of the buttons and hands that room back to the card. */
  readonly contentDataTour?: string
  /** data-tour anchor on the primary button */
  readonly primaryDataTour?: string
  /** data-tour anchor on the secondary button */
  readonly secondaryDataTour?: string
  /** Disable the primary action button (e.g. during a guided lesson) */
  readonly primaryDisabled?: boolean
}

const ICON_SRCS: Record<ConfirmDialogIconVariant, string> = {
  warning: AlertIcon,
  success: SuccessIcon,
}

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  iconVariant,
  titleVariant = 'primary',
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  secondaryIcon,
  dataTour,
  contentDataTour,
  primaryDataTour,
  secondaryDataTour,
  primaryDisabled,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange ?? ((v) => !v && onSecondary?.())}>
      <DialogContent
        className="w-80 max-w-[calc(100vw-32px)] rounded-xl p-4 text-center"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center gap-2" data-tour={dataTour}>
          <div className="flex w-full flex-col items-center gap-2" data-tour={contentDataTour}>
            {iconVariant && (
              <img src={ICON_SRCS[iconVariant]} alt="" className="size-12 object-contain" />
            )}
            <DialogHeader>
              <DialogTitle
                className={cn(
                  'text-center text-lg font-semibold',
                  titleVariant === 'default' ? 'text-foreground' : 'text-primary',
                )}
              >
                {title}
              </DialogTitle>
            </DialogHeader>
            {description}
          </div>
          <div className="mt-2 flex w-full flex-col gap-3">
            <Button
              onClick={onPrimary}
              className="h-12 rounded-full"
              data-tour={primaryDataTour}
              disabled={primaryDisabled}
            >
              {primaryLabel}
            </Button>
            {secondaryLabel && onSecondary && (
              <Button
                variant="outline"
                onClick={onSecondary}
                className="h-12 gap-2 rounded-full"
                data-tour={secondaryDataTour}
              >
                {secondaryIcon}
                {secondaryLabel}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export type { ConfirmDialogProps }
export { ConfirmDialog }
